import { Injectable, OnDestroy } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { defaultLanguageKey } from 'src/app/shared/models/language.dto';
import { CookieService } from './cookie.service';

type EmbedWindow = Window & { __amuveeEmbedLoaded?: boolean };

const EMBED_HOST_SELECTOR = '[data-amuvee-embed]';

// The loader ships UI strings for these only; anything else falls back to German.
const EMBED_LOCALES: readonly string[] = ['de', 'en', 'uk', 'ru'];

@Injectable({
  providedIn: 'root'
})
export class ChatbotService implements OnDestroy {
  private isLoaded = false;
  private isInitialized = false;
  private consentSubscription?: Subscription;
  private routerSubscription?: Subscription;
  private currentRoute = '';

  // Routes where chatbot should NOT be displayed
  private excludedRoutes = [
    '/admin',           
    '/user/dashboard', 
    '/user/profile',
    '/auth/login',
    '/auth/register',
    '/auth/check-inbox',
    '/auth/activate',
    '/auth/password-change'
  ];

  constructor(
    private cookieService: CookieService,
    private router: Router,
    private translate: TranslateService
  ) {
    // Expose to window for debugging (development only)
    if (!environment.production) {
      (window as any).chatbotService = this;
    }
    
    // Listen for consent changes
    this.consentSubscription = this.cookieService.consentChanges$.subscribe((rights) => {
      if (rights?.data_protection && !this.isLoaded) {
        this.loadChatbot();
        this.hideDisabledChatbot();
      } else if (!rights?.data_protection && this.isLoaded) {
        this.unloadChatbot();
        this.showDisabledChatbot();
      } else if (!rights?.data_protection && !this.isLoaded) {
        this.showDisabledChatbot();
      }
    });

    // Listen for route changes
    this.routerSubscription = this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event) => {
        this.currentRoute = (event as NavigationEnd).url;
        this.handleRouteChange();
      });
  }

  ngOnDestroy(): void {
    if (this.consentSubscription) {
      this.consentSubscription.unsubscribe();
    }
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
  }

  /**
   * Handles route changes and shows/hides chatbot accordingly
   */
  private handleRouteChange(): void {
    if (this.isRouteExcluded(this.currentRoute)) {
      // Hide chatbot on excluded routes
      if (this.isLoaded) {
        this.unloadChatbot();
      }
    } else {
      // Show chatbot on allowed routes (if consent is given)
      if (this.cookieService.rights?.data_protection && !this.isLoaded) {
        this.loadChatbot();
      }
    }
  }

  /**
   * Checks if the current route should exclude the chatbot
   */
  private isRouteExcluded(route: string): boolean {
    return this.excludedRoutes.some(excludedRoute => 
      route.startsWith(excludedRoute)
    );
  }

  /**
   * Adds a route to the exclusion list
   */
  addExcludedRoute(route: string): void {
    if (!this.excludedRoutes.includes(route)) {
      this.excludedRoutes.push(route);
    }
  }

  /**
   * Removes a route from the exclusion list
   */
  removeExcludedRoute(route: string): void {
    const index = this.excludedRoutes.indexOf(route);
    if (index > -1) {
      this.excludedRoutes.splice(index, 1);
    }
  }

  /**
   * Gets the list of excluded routes
   */
  getExcludedRoutes(): string[] {
    return [...this.excludedRoutes];
  }

  /**
   * Loads the Amuvee chat/voicebot embed widget if data protection consent is given
   */
  loadChatbot(): void {
    // Check if data protection consent is given
    if (!this.cookieService.rights?.data_protection) {
      return;
    }

    // Check if current route is excluded
    if (this.isRouteExcluded(this.currentRoute)) {
      return;
    }

    // Check if already loaded
    if (this.isLoaded) {
      return;
    }

    // Check if environment has chatbot configuration
    if (!environment.chatbot?.enabled || !environment.chatbot?.embedScriptUrl) {
      return;
    }

    try {
      this.initializeChatbot(environment.chatbot.embedScriptUrl);
      this.isLoaded = true;
      // Callers don't all pair loadChatbot() with hideDisabledChatbot() (e.g. cookie-banner.component.ts)
      // — the disabled placeholder must never survive alongside the real widget.
      this.hideDisabledChatbot();
    } catch (error) {
      // Silently handle errors in production
    }
  }

  /**
   * Unloads the chatbot widget
   */
  unloadChatbot(): void {
    if (!this.isLoaded) {
      return;
    }

    try {
      document.querySelector(EMBED_HOST_SELECTOR)?.remove();
      this.embedScriptElement()?.remove();
      // The loader no-ops while this guard is set, so a re-mount needs it cleared.
      delete (window as EmbedWindow).__amuveeEmbedLoaded;

      this.isLoaded = false;
      this.isInitialized = false;
    } catch (error) {
      // Silently handle errors in production
    }
  }

  /**
   * Shows a disabled chatbot button when data protection is not accepted
   */
  showDisabledChatbot(): void {
    // Chatbot is disabled in this environment — don't show the disabled-state icon either.
    if (!environment.chatbot?.enabled) {
      return;
    }

    // Remove any existing disabled chatbot
    this.hideDisabledChatbot();

    // Check if current route is excluded
    if (this.isRouteExcluded(this.currentRoute)) {
      return;
    }

    // Mirrors the real widget.js bubble exactly (56x56, bottom/right 24px, same
    // teal + icon + shadow) so swapping to the enabled widget after consent is
    // seamless — no theme-variable color here, the real widget never uses one.
    const disabledChatbot = document.createElement('div');
    disabledChatbot.id = 'disabled-chatbot';
    disabledChatbot.innerHTML = `
      <button type="button" aria-label="Chat öffnen" style="
        position: fixed;
        bottom: 24px;
        right: 24px;
        width: 56px;
        height: 56px;
        border: 0;
        border-radius: 50%;
        background: #017483;
        color: #fff;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 0;
        box-shadow: 0 10px 15px -3px rgba(0,0,0,.2), 0 4px 6px -4px rgba(0,0,0,.2);
        z-index: 2147483000;
        transition: transform .2s ease;
      " onmouseover="this.style.transform='scale(1.05)'" onmouseout="this.style.transform='scale(1)'">
        <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" style="width: 26px; height: 26px;">
          <path fill-rule="evenodd" clip-rule="evenodd" d="M4.848 2.771A49.144 49.144 0 0112 2.25c2.43 0 4.817.178 7.152.52 1.978.292 3.348 2.024 3.348 3.97v6.02c0 1.946-1.37 3.678-3.348 3.97a48.901 48.901 0 01-3.476.383.39.39 0 00-.297.17l-2.755 4.133a.75.75 0 01-1.248 0l-2.755-4.133a.39.39 0 00-.297-.17 48.9 48.9 0 01-3.476-.384c-1.978-.29-3.348-2.024-3.348-3.97V6.741c0-1.946 1.37-3.68 3.348-3.97z"/>
        </svg>
      </button>
    `;

    disabledChatbot.addEventListener('click', () => {
      this.openCookieBannerForChatbot();
    });

    document.body.appendChild(disabledChatbot);
  }

  /**
   * Hides the disabled chatbot button
   */
  hideDisabledChatbot(): void {
    const existingDisabledChatbot = document.getElementById('disabled-chatbot');
    if (existingDisabledChatbot) {
      existingDisabledChatbot.remove();
    }
  }

  /**
   * Opens cookie banner with chatbot message
   */
  openCookieBannerForChatbot(): void {
    // This will be called from the cookie banner dialog component
    if ((window as any).cookieBannerDialog) {
      (window as any).cookieBannerDialog.openWithChatbotMessage();
    }
  }

  /**
   * Reloads the chatbot based on current consent status
   */
  reloadChatbot(): void {
    this.unloadChatbot();
    this.loadChatbot();
  }

  /**
   * Checks if chatbot is currently loaded
   */
  isChatbotLoaded(): boolean {
    return this.isLoaded;
  }

  /**
   * Manual test method - call this from browser console to test chatbot loading
   * Usage: In browser console, type: window.chatbotService.testLoad()
   */
  testLoad(): void {
    // Only log in development mode
    if (!environment.production) {
      console.log('🧪 Testing chatbot load manually...');
      console.log('🧪 Current rights:', this.cookieService.rights);
      console.log('🧪 Environment config:', environment.chatbot);
    }
    this.loadChatbot();
  }

  /**
   * Initializes the chatbot widget
   */
  private initializeChatbot(embedScriptUrl: string): void {
    if (this.isInitialized) {
      return;
    }

    const script = document.createElement('script');
    script.src = embedScriptUrl;
    script.async = true;
    // Read by the loader as it executes, so it must be set before it is appended.
    script.dataset['locale'] = this.embedLocale();

    script.onerror = () => {
      this.isLoaded = false;
    };

    script.onload = () => {
      this.isInitialized = true;
    };

    document.body.appendChild(script);
  }

  private embedLocale(): string {
    const activeLanguage = this.translate.currentLang || this.translate.defaultLang;
    return EMBED_LOCALES.includes(activeLanguage) ? activeLanguage : defaultLanguageKey;
  }

  private embedScriptElement(): HTMLScriptElement | null {
    return document.querySelector(
      `script[src="${environment.chatbot?.embedScriptUrl}"]`
    );
  }

}
